import { useEffect, useState, useTransition } from "react";
import { Building2, Check, Copy, Handshake, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDict, fill } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";

type PartnershipItem = {
  id: string;
  invited_email: string;
  status: string;
  direction: "sent" | "received";
  client_company_name: string | null;
  agency_company_name: string | null;
  invite_token: string;
};

type InviteResult = {
  error?: string;
  link?: string;
  emailed?: boolean;
};

export function PartnersPage() {
  const t = useDict();
  const [items, setItems] = useState<PartnershipItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, startTransition] = useTransition();
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);

  function load() {
    setLoading(true);
    fetch("/api/partnerships", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json() as Promise<{ items: PartnershipItem[] }>;
      })
      .then((data) => setItems(data.items))
      .catch(() => setError(t.errors.networkError))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  function onInvite(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "");
    setInviteResult(null);
    startTransition(async () => {
      const res = await fetch("/api/partnerships/invite", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json()) as InviteResult & {
        error?: string;
        code?: string;
        link?: string;
        emailed?: boolean;
      };
      if (!res.ok || data.error) {
        setInviteResult({
          error: resolveApiError(t, data, "inviteFailed"),
        });
        return;
      }
      setInviteResult({
        link: data.link,
        emailed: data.emailed,
      });
      (e.target as HTMLFormElement).reset();
      load();
    });
  }

  function onRevoke(id: string) {
    if (!window.confirm("Revoke this partnership?")) return;
    startTransition(async () => {
      await fetch(`/api/partnerships/${id}/revoke`, {
        method: "POST",
        credentials: "include",
      });
      load();
    });
  }

  function statusLabel(status: string) {
    if (status === "pending") return t.partners.statusPending;
    if (status === "active") return t.partners.statusActive;
    return t.partners.statusRevoked;
  }

  function statusTone(status: string): "amber" | "green" | "neutral" {
    if (status === "pending") return "amber";
    if (status === "active") return "green";
    return "neutral";
  }

  if (loading && !items) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }

  if (error || !items) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.partners.title}</h1>
        <p className="text-muted">{t.partners.subtitle}</p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
              <Handshake className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-semibold">{t.partners.inviteTitle}</h2>
              <p className="text-sm text-muted">{t.partners.inviteHint}</p>
            </div>
          </div>
          <form onSubmit={onInvite} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="partner-email">{t.partners.emailLabel}</Label>
              <Input
                id="partner-email"
                name="email"
                type="email"
                required
                placeholder={t.partners.emailPlaceholder}
              />
            </div>
            <Button type="submit" loading={pending}>
              <Send className="h-4 w-4" /> {t.partners.sendInvite}
            </Button>
          </form>
          {inviteResult?.error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {inviteResult.error}
            </p>
          )}
          {inviteResult?.link && (
            <div className="space-y-2 rounded-xl border border-border bg-surface p-3">
              <p
                className={`text-sm font-medium ${
                  inviteResult.emailed ? "text-emerald-700" : "text-amber-700"
                }`}
              >
                {inviteResult.emailed
                  ? t.partners.inviteSent
                  : t.partners.notEmailed}
              </p>
              <p className="text-xs text-muted">{t.partners.inviteLink}</p>
              <div className="flex items-center gap-2">
                <span className="flex-1 truncate text-sm">{inviteResult.link}</span>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteResult.link!);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-semibold">{t.partners.listTitle}</h2>
        {items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <Building2 className="h-10 w-10 text-muted/60" />
              <p className="mt-4 font-medium">{t.partners.empty}</p>
              <p className="text-sm text-muted">{t.partners.emptyDesc}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="divide-y divide-border pt-2">
              {items.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium">
                      {p.direction === "sent"
                        ? p.agency_company_name || p.invited_email
                        : p.client_company_name || p.invited_email}
                    </p>
                    <p className="text-sm text-muted">
                      {p.invited_email} ·{" "}
                      {p.direction === "sent"
                        ? t.partners.directionSent
                        : t.partners.directionReceived}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={statusTone(p.status)}>
                      {statusLabel(p.status)}
                    </Badge>
                    {(p.status === "pending" || p.status === "active") && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pending}
                        onClick={() => onRevoke(p.id)}
                      >
                        {t.partners.revoke}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
